import React from 'react';

import { useClientStore } from 'apps/hooks';
import {
  Checkbox,
  Divisor,
  Icon,
  Button,
  Radio,
  SvgIcon,
  Tooltip,
  Box,
} from 'components';

import { Clock } from 'icons';
import { routerStore } from 'core';
import { TeamMember } from './subscriptionManager';
import { Counter } from '../../../components/Counter/index';
import { staticURL } from '../../../core/url';
import { NextPaymentFreeBar } from './nextPaymentFreeBar';

interface AddCollaboratorsProps {
  teamMembers: TeamMember[];
  billingCycle: 'yearly' | 'monthly';
  teamName: string;
  nextPaymentDate: string;
  totalPrice: number;
  totalEditorsPrice: number;
  totalIncludedEditorsPrice: number;
  productPrice: number;
  seats: number;
  productType: 'pro' | 'business';
  viewersNumber: number;
  billingMonths: number;
  additionalPrice: number;
  additionalSeats: number;
  includedPlanSeats: number;
  outsideCollaborators: number;
  componentMode: 'edit' | 'readOnly';
  annualPriceDetails?: {
    unsavedPercentage: number;
    productPriceByDiscount: number;
    originPrice: number;
  };

  onAddAdditionalEditors: (editorNumber: number) => void;
  onChooseEditors: (
    name: string,
    role: 'owner' | 'admin' | 'editor' | 'viewer' | null,
  ) => void;
  onGoToPaymentInformation?: () => void;
  onBillingCycleChange?: (billingCycle: 'yearly' | 'monthly') => void;
}

const TeamMemberCheckbox = ({
  member,
  onChooseEditors,
  disableCheckbox,
}: {
  member: TeamMember;
  onChooseEditors: AddCollaboratorsProps['onChooseEditors'];
  disableCheckbox: (usernameOrEmail: string) => boolean;
}) => {
  const usernameOrEmail = member.username || member.email;
  return (
    <div key={usernameOrEmail} className='flex justify-between items-end mt-2'>
      <div
        className='text-black flex items-center relative'
        style={{ top: '-7px' }}
        data-testid={`addCollaboratorsPage-${usernameOrEmail}-user`}
      >
        {member.role === null && member.isFromProject && (
          <Tooltip delayShow={200} place='top' id={usernameOrEmail} />
        )}
        <span
          className={`mr-1 absolute flex justify-center items-center  ${
            member.role === null ? 'custom-line-through' : ''
          }`}
          data-tip='This user will still have access to the project'
          data-for={usernameOrEmail}
        >
          {usernameOrEmail}
          {member.isPendingInvited && (
            <>
              <Tooltip place='right' delayShow={200} id='permissionsTooltip' />
              <Box
                pt={2}
                data-tip='Pending confirmation'
                data-for='permissionsTooltip'
                className='ml-2'
              >
                <SvgIcon icon={<Clock />} />
              </Box>
            </>
          )}
        </span>
      </div>
      <div className='flex w-64 justify-end'>
        <span style={{ flex: 0.5 }} className='text-end'>
          <Checkbox
            id={`${usernameOrEmail}-owner`}
            dataTestId={`${usernameOrEmail}-owner`}
            value={`${usernameOrEmail}-owner`}
            onChange={() => {
              if (onChooseEditors) {
                onChooseEditors(
                  usernameOrEmail,
                  member.role !== 'owner' ? 'owner' : null,
                );
              }
            }}
            disabled={disableCheckbox(usernameOrEmail)}
            checked={member.role === 'owner'}
          />
        </span>
        <span style={{ flex: 0.5 }} className='text-end'>
          <Checkbox
            id={`${usernameOrEmail}-admin`}
            dataTestId={`${usernameOrEmail}-admin`}
            value={`${usernameOrEmail}-admin`}
            onChange={() => {
              if (onChooseEditors) {
                onChooseEditors(
                  usernameOrEmail,
                  member.role !== 'admin' ? 'admin' : null,
                );
              }
            }}
            disabled={disableCheckbox(usernameOrEmail)}
            checked={member.role === 'admin'}
          />
        </span>
        <span style={{ flex: 0.5 }} className='text-end'>
          <Checkbox
            dataTestId={`${usernameOrEmail}-editor`}
            id={`${usernameOrEmail}-editor`}
            value={`${usernameOrEmail}-editor`}
            onChange={() => {
              if (onChooseEditors) {
                onChooseEditors(
                  usernameOrEmail,
                  member.role !== 'editor' ? 'editor' : null,
                );
              }
            }}
            disabled={disableCheckbox(usernameOrEmail)}
            checked={member.role === 'editor'}
          />
        </span>
        <span style={{ flex: 0.5 }} className='text-end'>
          <Checkbox
            dataTestId={`${usernameOrEmail}-viewer`}
            id={`${usernameOrEmail}-viewer`}
            value={`${usernameOrEmail}-viewer`}
            onChange={() => {
              if (onChooseEditors) {
                onChooseEditors(
                  usernameOrEmail,
                  member.role === 'viewer' ? null : 'viewer',
                );
              }
            }}
            disabled={disableCheckbox(usernameOrEmail)}
            checked={member.role === 'viewer'}
          />
        </span>
      </div>
    </div>
  );
};
export const AddCollaborators = (props: AddCollaboratorsProps) => {
  const {
    teamMembers,
    onAddAdditionalEditors,
    onChooseEditors,
    productType,
    billingCycle,
    annualPriceDetails,
    onBillingCycleChange,
    totalPrice,
    totalEditorsPrice,
    totalIncludedEditorsPrice,
    includedPlanSeats,
    productPrice,
    billingMonths,
    viewersNumber,
    additionalPrice,
    additionalSeats,
    onGoToPaymentInformation,
    outsideCollaborators,
    seats,
    teamName,
    componentMode,
    nextPaymentDate,
  } = props;

  const isChangePlan = routerStore.route.name === 'workspace.upgrade';

  const clientStore = useClientStore();

  const { unsavedPercentage, productPriceByDiscount, originPrice } =
    annualPriceDetails;

  const handleBillingCycleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onBillingCycleChange) {
      onBillingCycleChange(e.target.value as 'yearly' | 'monthly');
    }
  };

  const productTypeTextMap = {
    pro: 'Pro',
    business: 'Business',
  };

  const productTypeText = productTypeTextMap[productType];
  const readOnly = componentMode === 'readOnly';

  const disableCheckbox = (usernameOrEmail: string) => {
    if (readOnly) {
      return true;
    }

    return (
      usernameOrEmail &&
      (usernameOrEmail === clientStore.signedInProfile.username ||
        usernameOrEmail === clientStore.signedInProfile.email)
    );
  };

  const { isMobile } = clientStore;

  return (
    <div className='flex w-full'>
      {!isMobile && (
        <div className='px-48' style={{ width: '70vw', minWidth: '800px' }}>
          <h1
            className={`${
              isMobile ? 'text-2xl' : ' text-3xl'
            } text-black text-center`}
          >
            {`${readOnly ? 'Review' : 'Choose'} Your Wikifactory Editors`}
          </h1>
          <span className='text-black'>
            Unchecked users will still be able to view and comment on files
          </span>
          <div className='flex justify-between mt-4'>
            <span
              className='text-black font-extrabold'
              data-testid='addCollaboratorsPage-teamName-text'
            >
              {teamName}
            </span>
            <div className='flex w-64 justify-end'>
              <span
                data-testid='createTeamPage-owner-number-text'
                style={{ flex: 0.5 }}
                className='text-black text-end'
              >
                Owners
              </span>
              <span
                data-testid='createTeamPage-admin-number-text'
                style={{ flex: 0.5 }}
                className='text-black text-end'
              >
                Admins
              </span>
              <span
                data-testid='createTeamPage-editor-number-text'
                style={{ flex: 0.5 }}
                className='text-black text-end'
              >
                Editors
              </span>
              <span
                data-testid='createTeamPage-editor-number-text'
                style={{ flex: 0.5 }}
                className='text-black text-end'
              >
                Viewers
              </span>
            </div>
          </div>
          <div className='my-6 pb-2 max-h-72 h-fit overflow-auto'>
            {teamMembers
              .filter(member => !member.isFromProject)
              .map(member => {
                return (
                  <TeamMemberCheckbox
                    member={member}
                    key={member.username || member.email}
                    onChooseEditors={onChooseEditors}
                    disableCheckbox={disableCheckbox}
                  />
                );
              })}

            {teamMembers.filter(member => member.isFromProject).length > 0 ? (
              <>
                <h3 className='mt-8'>Members From Transfer Projects</h3>
                <div
                  className='mb-1'
                  data-testid='addCollaborators-project-members'
                >
                  {teamMembers
                    .filter(member => member.isFromProject)
                    .map(member => {
                      return (
                        <TeamMemberCheckbox
                          member={member}
                          key={member.username || member.email}
                          onChooseEditors={onChooseEditors}
                          disableCheckbox={disableCheckbox}
                        />
                      );
                    })}
                </div>
              </>
            ) : null}
          </div>
          <Divisor />
          {componentMode === 'edit' ? (
            <>
              <div>
                <span className='text-black font-extrabold'>
                  Add Additional Editors
                </span>
                <div className='flex items-center'>
                  <Counter
                    decDataTestId='addCollaborators-dec-button'
                    incDataTestId='addCollaborators-inc-button'
                    countDataTestId='addCollaborators-counter-text'
                    className='mt-2 mb-4'
                    count={additionalSeats}
                    onCountChange={onAddAdditionalEditors}
                  />
                  <div className='ml-4 text-xs'>
                    <div data-testid='addCollaboratorsPage-additionalTotal-text'>
                      €{additionalPrice}
                    </div>
                    <div data-testid='addCollaboratorsPage-additionalDetail-text'>
                      {billingCycle === 'yearly' ? (
                        <>
                          <span className='line-through'>€{productPrice}</span>
                          <span className='mx-1'>
                            €{productPriceByDiscount}
                          </span>
                        </>
                      ) : (
                        <span className='mr-1'>€{productPrice}</span>
                      )}
                      x {additionalSeats} editors
                    </div>
                  </div>
                </div>
              </div>
              <Divisor />
            </>
          ) : (
            <>
              <div className='text-black font-extrabold my-2'>
                <span data-testid='addCollaboratorPage-additionalSeatsNumber-text'>
                  {additionalSeats} Additional editors
                </span>
                {outsideCollaborators > 0 && (
                  <span className='text-black text-xs font-normal ml-1'>
                    ({outsideCollaborators} outside collaborators)
                  </span>
                )}
              </div>
              <Divisor />
            </>
          )}
          <div className='text-black mt-2'>
            <span>*Unlimited viewers included for free</span>
          </div>
          <div className='mt-2'>
            <span className='font-extrabold'>
              View{' '}
              <a
                className='underline decoration-1 cursor-pointer text-black mr-px'
                href='https://wikifactory.com/platform/roles-and-permissions/'
                target='_blank'
                rel='noreferrer'
              >
                ‘Roles & Access Permissions’
              </a>
              Breakdown
              <Icon className='ml-2' name='arrow_right' />
            </span>
          </div>
        </div>
      )}

      <div
        className='w-px bg-gray-400'
        style={{
          height: 'calc(100vh - 76px)',
        }}
      />
      <div
        className='px-8 mt-4'
        style={{
          minWidth: isMobile ? 'unset' : '400px',
          width: isMobile ? '100vw' : '30vw',
        }}
      >
        <div className='flex items-center'>
          <img
            className='rounded-full w-10 h-10'
            src={staticURL('assets/logos/wikifactory-black.png')}
          />
          <div className='ml-2'>
            <div
              className='text-black text-xl font-extrabold'
              data-testid='addCollaboratorsPage-plan-text'
            >
              {productTypeText} Plan
            </div>
            <div className='text-black text-sm mt-1'>Subscription</div>
          </div>
        </div>
        <div className='flex justify-between mt-4 mb-2'>
          <span className='text-black font-extrabold'>Currency</span>
          <span data-testid='addCollaboratorsPage-currency-text'>€ EURO</span>
        </div>
        <Divisor />
        <div className='text-black font-extrabold text-16 mt-2'>
          Billing Cycle
        </div>
        <div className='flex justify-between' style={{ marginLeft: '-8px' }}>
          <Radio
            onChange={handleBillingCycleChange}
            label='Annual'
            id='annual'
            labelsAlignment='justify'
            groupName='materials'
            value='yearly'
            size='small'
            checked={billingCycle === 'yearly'}
          />

          <Radio
            onChange={handleBillingCycleChange}
            className='ml-8'
            id='monthly'
            label='Monthly'
            labelsAlignment='justify'
            groupName='materials'
            value='monthly'
            size='small'
            checked={billingCycle === 'monthly'}
          />
        </div>
        {/* <SaveUpBadge className='mb-2' /> */}
        <Divisor />
        <div className='flex  text-black justify-between my-2'>
          <span data-testid='addCollaboratorsPage-editorsSummary-text'>
            {seats} Wikifactory Editors
          </span>
          {billingCycle === 'yearly' ? (
            <div>
              <span
                className='line-through'
                data-testid='addCollaboratorsPage-annualOriginPrice-text'
              >
                €{originPrice}
              </span>
              <span
                className='font-extrabold ml-1'
                data-testid='addCollaboratorsPage-annualTotalPrice-text'
              >
                €{totalEditorsPrice}
              </span>
            </div>
          ) : (
            <span
              className='font-extrabold'
              data-testid='addCollaboratorsPage-monthlyTotalPrice-text'
            >
              €{totalEditorsPrice}
            </span>
          )}
        </div>

        <div className='flex justify-between items-center h-8'>
          <span className='text-xs'>
            {billingCycle === 'yearly' ? (
              <>
                <span
                  className='line-through'
                  data-testid='addCollaboratorsPage-annualOriginProductPrice-text'
                >
                  €{productPrice}
                </span>
                <span
                  className='mx-1'
                  data-testid='addCollaboratorsPage-annualDiscountProductPrice-text'
                >
                  €{productPriceByDiscount}
                </span>
              </>
            ) : (
              <span
                className='mr-1'
                data-testid='addCollaboratorsPage-monthProductPrice-text'
              >
                €{productPrice}
              </span>
            )}
            <span data-testid='addCollaboratorsPage-editorCalcDetail-text'>
              x {seats} editors x {billingMonths} months
            </span>
          </span>
          {billingCycle === 'yearly' && unsavedPercentage && (
            <span className='text-12 text-blue-500 bg-blue-100 rounded-full h-5 px-2'>
              {unsavedPercentage}% off
            </span>
          )}
        </div>

        <div className='flex text-black justify-between my-2'>
          <span data-testid='addCollaboratorsPage-viewersSummary-text'>
            {viewersNumber} Wikifactory Viewers
          </span>
          <span
            className='font-extrabold ml-1'
            data-testid='addCollaboratorsPage-freeTrailTotalPrice-text'
          >
            €{0}
          </span>
        </div>

        <div className='flex justify-between items-center h-8'>
          <span className='text-xs'>
            <span
              className='mr-1'
              data-testid='addCollaboratorsPage-monthViewerProductPrice-text'
            >
              €0
            </span>
            <span data-testid='addCollaboratorsPage-viewersCalcDetail-text'>
              x {viewersNumber} viewers x {billingMonths} months
            </span>
          </span>
        </div>
        <Divisor />

        {includedPlanSeats > 0 && (
          <>
            <div className='flex justify-between mt-2'>
              <span className='font-extrabold'>Included Seats</span>
              <span
                className='font-extrabold'
                data-testid='addCollaboratorsPage-include-editors-price-text'
              >
                - €{totalIncludedEditorsPrice} EUR
              </span>
            </div>
            <div>
              <span
                data-testid='addCollaboratorsPage-include-editors-text'
                className='text-xs'
              >
                {includedPlanSeats} editors
              </span>
            </div>
          </>
        )}
        {isChangePlan ? (
          <div className='flex justify-between mt-2'>
            <span className='font-extrabold'>Subtotal</span>
            <span
              className='font-extrabold'
              data-testid='addCollaboratorsPage-subtotal-text'
            >
              €{totalPrice}
            </span>
          </div>
        ) : (
          <div>
            <div className='flex justify-between mt-2'>
              <span className='text-black font-extrabold text-16'>
                Free Trial Total
              </span>
              <span
                className='font-extrabold'
                data-testid='addCollaboratorsPage-freeTrailSubtotal-text'
              >
                €0
              </span>
            </div>
            <NextPaymentFreeBar
              totalPrice={totalPrice}
              nextPaymentDate={nextPaymentDate}
            />
          </div>
        )}

        <div className='text-center mt-8'>
          <Button
            dataTestId='addCollaborators-gotoPaymentInfo-button'
            className='text-center w-full'
            text='Review'
            variant='tertiary'
            icon='arrow_right'
            iconPosition='right'
            onClick={() => {
              if (onGoToPaymentInformation) {
                onGoToPaymentInformation();
              }
            }}
          />
        </div>
        {!isChangePlan && (
          <p className='text-black text-center my-4 text-sm'>
            You can cancel anytime.
          </p>
        )}
      </div>
    </div>
  );
};
